'use client';

import { DeploymentMode, Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

type Option = {
  id: DeploymentMode;
  label: string;
  desc: string;
  bullets: string[];
};

const OPTIONS: Option[] = [
  {
    id: 'standalone',
    label: 'Standalone website',
    desc: 'Participants come directly to your study link. The AI handles the full intro, consent, and ending.',
    bullets: [
      'Full participant brief and consent shown before the interview',
      'Summary, feedback, and contact info shown at the end',
      'Best for: recruiting on social media, mailing lists, or word of mouth',
    ],
  },
  {
    id: 'embedded',
    label: 'Embedded in a survey (e.g. Qualtrics)',
    desc: 'Participants arrive from your survey, do the voice interview, then return to finish the survey.',
    bullets: [
      'Short welcome — your survey already handled consent',
      'Automatic redirect back to your survey when the interview ends',
      'Best for: existing survey panels, IRB-approved Qualtrics studies',
    ],
  },
];

export function Step2Deployment({ data, setData, onBack, onNext }: Props) {
  const selected = data.deploymentMode;

  return (
    <StepWrap>
      <StepHead
        label="Step 2 — Deployment"
        title="Where will participants reach this study?"
        subtitle="This shapes what we ask you about next. Standalone studies need a participant brief and consent flow; embedded studies inherit those from your host survey."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {OPTIONS.map((opt) => {
          const on = selected === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setData({ ...data, deploymentMode: opt.id })}
              style={{
                border: `1.5px solid ${on ? T.terra : T.creamBorder}`,
                borderRadius: 12,
                padding: '18px 20px',
                background: on ? T.terraLight : T.white,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}
              type="button"
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  flexShrink: 0,
                  marginTop: 3,
                  border: `2px solid ${on ? T.terra : T.creamBorder}`,
                  background: on ? T.terra : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {on && (
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: T.white,
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: T.espresso }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: 14, color: T.espressoMid, lineHeight: 1.55 }}>
                  {opt.desc}
                </div>
                <ul
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    paddingLeft: 0,
                    listStyle: 'none',
                    marginTop: 4,
                  }}
                >
                  {opt.bullets.map((b, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: 13,
                        color: T.espressoLight,
                        lineHeight: 1.5,
                        display: 'flex',
                        gap: 8,
                      }}
                    >
                      <span style={{ color: T.terra, marginTop: 1 }}>·</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          fontSize: 13,
          color: T.espressoLight,
          fontStyle: 'italic',
        }}
      >
        You can change this later before publishing.
      </div>

      <Nav onBack={onBack} onNext={onNext} nextDisabled={!selected} />
    </StepWrap>
  );
}
